import React, { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import "quill-emoji";
import { loadJiraUsers } from "../../api/jira.service";

// Register MentionBlot
const InlineEmbed = Quill.import("blots/embed");

class MentionBlot extends InlineEmbed {
  static blotName = "mention";
  static tagName = "span";

  static create(value) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-id", value.id);
    node.setAttribute("data-value", value.value);

    node.classList.add("ql-mention");
    node.innerHTML = `
      <span style="
        display: inline-flex;
        align-items: center;
        background: #e0f2f1;
        padding: 4px 8px;
        border-radius: 16px;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        font-weight: 500;
        font-size: 13px;
        line-height: 1;
        white-space: nowrap;
      ">
        <span>@${value.value}</span>
      </span>
    `;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute("data-id"),
      value: node.getAttribute("data-value"),
    };
  }
}

Quill.register(MentionBlot);

const RichTextEditor = ({ value, onChange }) => {
  const quillRef = useRef(null);
  const [editorValue, setEditorValue] = useState(value || "");
  const [mentionQuery, setMentionQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [mentionValues, setMentionValues] = useState([]);
  const [mentionHistory, setMentionHistory] = useState([]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["emoji"],
    ],
    "emoji-toolbar": true,
    "emoji-shortname": true,
    "emoji-textarea": true,
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
    "emoji",
    "mention",
  ];

  const sanitizeEditorValue = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const invalidMentions = tempDiv.querySelectorAll(
      'span.ql-mention[data-id="null"], span.ql-mention[data-value="null"], span.ql-mention[data-id="undefined"], span.ql-mention[data-value="undefined"]'
    );
    invalidMentions.forEach((span) => span.remove());

    return tempDiv.innerHTML;
  };

  const handleChange = (content, delta, source, editor) => {
    const sanitizedContent = sanitizeEditorValue(content);
    setEditorValue(sanitizedContent);
    onChange?.(sanitizedContent);

    const range = editor.getSelection();
    if (!range) return;

    const textBeforeCursor = editor.getText(0, range.index);
    const match = textBeforeCursor.match(/@([a-zA-Z\s]*)$/);

    if (match) {
      const query = match[1];
      const bounds = editor.getBounds(range.index);

      const filtered =
        query === ""
          ? mentionHistory
          : mentionValues.filter((item) =>
              item.value.toLowerCase().includes(query.toLowerCase())
            );

      if (filtered.length === 0) {
        setShowDropdown(false);
        return;
      }

      setMentionQuery(query);
      setSuggestions(filtered);
      setDropdownPosition({ top: bounds.top + 30, left: bounds.left });
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const insertMention = (mention) => {
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection();
    if (!range) return;

    const cursorIndex = range.index;
    const textBefore = editor.getText(0, cursorIndex);
    const match = textBefore.match(/@([a-zA-Z\s]*)$/);

    if (match) {
      const atPosition = cursorIndex - match[0].length;
      editor.deleteText(atPosition, match[0].length);
      editor.insertEmbed(atPosition, "mention", {
        id: mention.id,
        value: mention.value,
      });
      editor.insertText(atPosition + 1, " ");
      editor.setSelection(atPosition + 2);
    }

    setMentionHistory((prev) => {
      const existing = prev.find((item) => item.id === mention.id);
      const updated = existing
        ? [mention, ...prev.filter((item) => item.id !== mention.id)]
        : [mention, ...prev];
      return updated.slice(0, 5);
    });

    setMentionQuery("");
    setSuggestions([]);
    setShowDropdown(false);
  };

  const preprocessDescriptionForEditor = (description, mentionList = []) => {
    if (!description) return "";

    const mentionRegex = /\[~accountid:(\d+):([a-f0-9\-]{36})\]/gi;
    description = description.replace(
      mentionRegex,
      (match, accountId, mentionId) => {
        const user = mentionList.find((u) => u.id === `${accountId}:${mentionId}`);
        if (user && user.id && user.value) {
          return `
            <span class="ql-mention" contenteditable="false" data-id="${user.id}" data-value="${user.value}">
              <span style="display:inline-flex;align-items:center;background:#e0f2f1;padding:4px 8px;border-radius:16px;box-shadow:0 1px 2px rgba(0,0,0,0.1);font-weight:500;font-size:13px;line-height:1;white-space:nowrap;">
                <span>@${user.value}</span>
              </span>
            </span>
          `;
        }
        return "";
      }
    );

    const lines = description.split(/\r?\n/);
    let html = "";
    let inOrderedList = false;
    let inUnorderedList = false;

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (/^#\s+/.test(trimmed)) {
        if (!inOrderedList) {
          if (inUnorderedList) {
            html += "</ul>";
            inUnorderedList = false;
          }
          html += "<ol>";
          inOrderedList = true;
        }

        const itemContent = trimmed.replace(/^#\s+/, "");
        const formattedContent = itemContent
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
          .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
          .replace(/_(.*?)_/g, "<em>$1</em>");
        html += `<li>${formattedContent}</li>`;
      } else if (/^(\*|\-)\s+/.test(trimmed)) {
        if (!inUnorderedList) {
          if (inOrderedList) {
            html += "</ol>";
            inOrderedList = false;
          }
          html += "<ul>";
          inUnorderedList = true;
        }

        const itemContent = trimmed.replace(/^(\*|\-)\s+/, "");
        const formattedContent = itemContent
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
          .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
          .replace(/_(.*?)_/g, "<em>$1</em>");
        html += `<li>${formattedContent}</li>`;
      } else {
        if (inOrderedList) {
          html += "</ol>";
          inOrderedList = false;
        }
        if (inUnorderedList) {
          html += "</ul>";
          inUnorderedList = false;
        }

        const formatted = trimmed
          .replace(/^h1\.\s?(.*)$/gm, "<h1>$1</h1>")
          .replace(/^h2\.\s?(.*)$/gm, "<h2>$1</h2>")
          .replace(/^h3\.\s?(.*)$/gm, "<h3>$1</h3>")
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
          .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
          .replace(/_(.*?)_/g, "<em>$1</em>");

        html += `<p>${formatted || "<br/>"}</p>`;
      }
    });

    if (inOrderedList) html += "</ol>";
    if (inUnorderedList) html += "</ul>";

    return html;
  };

  useEffect(() => {
    if (value && !value.includes('@')) {
      // If the value is plain HTML without mentions, set it directly
      setEditorValue(value);
    } else {
      // Process mentions if needed
      const html = preprocessDescriptionForEditor(value, mentionValues);
      const cleaned = sanitizeEditorValue(html);
      setEditorValue(cleaned);
    }
  }, [value]);

  // Remove the hasRun check from the value effect since we want to update on any value change
  useEffect(() => {
    if (value && mentionValues.length > 0) {
      const html = preprocessDescriptionForEditor(value, mentionValues);
      const cleaned = sanitizeEditorValue(html);
      setEditorValue(cleaned);
    }
  }, [mentionValues]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await loadJiraUsers();
        if (response) {
          const formatted = response.map((user) => ({
            id: user.accountId,
            value: user.name,
          }));
          setMentionValues(formatted);
        }
      } catch (err) {
        console.error("Error loading Jira users:", err);
      }
    };

    loadUsers();
  }, []);

  return (
    <div style={{ position: "relative", fontFamily: "Arial, sans-serif" }}>
      <ReactQuill
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        theme="snow"
        placeholder="Type @ to mention someone or use emojis..."
      />

      {showDropdown && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 100,
            minWidth: "180px",
            maxHeight: "220px",
            overflowY: "auto",
          }}
        >
          {suggestions.map((user) => (
            <div
              key={user.id}
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
                color: "#004D40",
                borderBottom: "1px solid #B2DFDB",
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertMention(user)}
            >
              @{user.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
